import {
  Button,
  CogFilledSVG,
  constants,
  Toast,
  useWindowSize,
} from "@newsela/angelou";
import {
  actionNames,
  actionPrefixes,
  objectTypes,
  withTrackEvent,
} from "@newsela/snitch";

import { handleHideTranslation } from "@/assignments/utils/assignmentActions";
import globalToasterRef from "@/base/globalToasterRef";
import AILunaTag from "@/components/AIComponents/AILunaTag/AILunaTag";
import ReportGeneratedText from "@/view/ReportGeneratedText";
import useAssignmentReducerContext, {
  type AssignModalProps,
} from "@/view/utils/useAssignmentReducerContext";
import useTranslatorControlsContext from "@/view/utils/useTranslatorControlsContext";
import ReactionButtons from "./ReactionButtons";
import {
  buttonContainerCss,
  disableFeatureToastButtonCss,
  disableFeatureToastTextCss,
  featureButtonCss,
  footerStyles,
  footerTextStyles,
} from "./TranslateDrawerFooter.styles";

const ButtonWithTrackEvent = withTrackEvent(Button);

interface TranslateDrawerFooterProps {
  contentId: string;
  extraData?: {
    target_language?: { code?: string; name?: string };
    text?: string;
    translation_identifier?: string;
  };
  identifier: string;
  isTeacher: boolean;
}

const TranslateDrawerFooter = ({
  contentId,
  extraData,
  identifier,
  isTeacher,
}: TranslateDrawerFooterProps) => {
  const { width: windowWidth } = useWindowSize();
  const { sm } = constants.breakpoints.Breakpoints;
  const isMobile = windowWidth <= sm;

  const { setIsTranslateMode } = useTranslatorControlsContext();

  const {
    assignModal: { assignModalTriggerRef, setIsAssignModalActive },
    dispatch,
  } = useAssignmentReducerContext() as {
    assignModal: AssignModalProps;
    dispatch: () => void;
  };

  const iconDisable = isMobile
    ? {}
    : {
        icon: {
          SvgComponent: CogFilledSVG,
          alignment: Button.iconAlignments.left,
        },
      };

  const renderToastMessage = () => {
    return (
      <p css={disableFeatureToastTextCss}>
        AI-translation disabled for this article. Turn it back on in your&nbsp;
        <ButtonWithTrackEvent
          __cssFor={{ root: disableFeatureToastButtonCss }}
          dataProps={{
            "data-qa-selector": "toast-assignment-settings-button",
          }}
          flavor={Button.flavors.incognito}
          onClick={() => setIsAssignModalActive(true)}
          objectType={objectTypes.generatedTextAsset}
          actionName={actionNames.navigate}
          actionPrefix={actionPrefixes.initiate}
          // @ts-expect-error ref from angelou doesn't have an accessible type
          ref={assignModalTriggerRef}
          streamProperties={{
            content_type: "Assignment",
            description: `User open create assignment settings from toast`,
            content_id: contentId,
          }}
        >
          assignment settings.
        </ButtonWithTrackEvent>
      </p>
    );
  };

  const EVENT_TOAST_TRANSLATE_CONTENT_DISABLED = {
    showIcon: true,
    statusColor: Toast.statusColor.success,
    children: renderToastMessage(),
  };

  const disableTranslation = () => {
    handleHideTranslation({ hide_translation: true }, dispatch);
    setIsTranslateMode(false);
    globalToasterRef.current.displayToast(
      EVENT_TOAST_TRANSLATE_CONTENT_DISABLED,
    );
  };

  const footerDisclaimer =
    "This translation was generated by AI. Mistakes are possible.";

  const teacherFooterText = `${footerDisclaimer} Students can only translate content if you assign this article.`;

  return (
    <>
      <div css={footerStyles}>
        <p css={footerTextStyles}>
          <AILunaTag /> {isTeacher ? teacherFooterText : footerDisclaimer}
        </p>
        {isTeacher ? (
          <div css={buttonContainerCss}>
            <ButtonWithTrackEvent
              __cssFor={{ root: featureButtonCss }}
              dataProps={{
                "data-qa-selector": "disable_translate_content",
              }}
              flavor={Button.flavors.muted}
              onClick={disableTranslation}
              objectType={objectTypes.generatedTextAsset}
              actionName={actionNames.hide}
              streamProperties={{
                content_type: "Article",
                description: `User clicked to disable translation using button in: ${identifier}`,
                content_id: contentId,
              }}
              {...iconDisable}
            >
              Disable this feature
            </ButtonWithTrackEvent>
            <ReportGeneratedText
              contentId={contentId}
              reportSource="Translate"
              identifier={identifier}
              extraData={extraData || {}}
            />
          </div>
        ) : (
          <ReactionButtons
            contentId={contentId}
            targetLanguage={extraData?.target_language?.code ?? ""}
            text={extraData?.text ?? ""}
            translationIdentifier={extraData?.translation_identifier ?? ""}
          />
        )}
      </div>
    </>
  );
};

export default TranslateDrawerFooter;
